import { Router } from "@frui.ts/screens";
import { Modules } from "config";
import OutageRepository from "repositories/outageRepository";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import SystemComponentOutageState from "entities/systemComponentOutageState";
import SystemComponent from "entities/systemComponent";
import OptionsService from "services/optionsService";
import ListViewModel from "../../listViewModel";
import OutageCreateViewModel from "./outageCreateViewModel";
import OutageDetailViewModel from "./outageDetailViewModel";
import GetSystemComponentOutagesRequest from "entities/getSystemComponentOutagesRequest";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { computed } from "mobx";
import SystemComponentOutageResponse from "entities/systemComponentOutageResponse";

const navigationName = "outages";

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [OutageDetailViewModel, OutageCreateViewModel],
})
export default class OutagesViewModel extends ListViewModel<
  SystemComponentOutageResponse,
  OutageDetailViewModel,
  OutageCreateViewModel,
  GetSystemComponentOutagesRequest
> {
  readonly modelName = "outage";

  @computed
  get systemComponentOptions() {
    return this.optionsService.entityOptions(SystemComponent);
  }

  @computed
  get stateOptions() {
    return this.optionsService
      .internalEnumOptions(SystemComponentOutageState)
      .filter(item => item.id !== SystemComponentOutageState.Unknown);
  }

  @computed
  get booleanOptions() {
    return [
      { id: true, label: this.localizationService.translateGeneral("boolean.true") },
      { id: false, label: this.localizationService.translateGeneral("boolean.false") },
    ];
  }

  @bound
  allowItemDetail(item: SystemComponentOutageResponse) {
    return !item.isDeleted;
  }

  @bound
  allowItemDelete(item: SystemComponentOutageResponse) {
    return !item.isDeleted;
  }

  constructor(
    protected createViewModelFactory: () => OutageCreateViewModel,
    protected detailViewModelFactory: ReturnType<typeof OutageDetailViewModel.Factory>,
    protected repository: OutageRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected createFilter(): GetSystemComponentOutagesRequest {
    return new GetSystemComponentOutagesRequest();
  }

  getSystemComponentName(systemComponentId: string) {
    return (
      this.optionsService.entityOptions(SystemComponent).find(systemComponent => systemComponent.id === systemComponentId)
        ?.label ?? ""
    );
  }

  translateState(state: SystemComponentOutageState) {
    return this.localizationService.translateEnum("systemComponentOutageState", state);
  }

  translateFilterAttribute(attribute: PropertyName<GetSystemComponentOutagesRequest>, size?: number) {
    return this.localizationService.translateAttribute("systemComponentOutageRequest", attribute, size);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `outages.${code}`, size);
  }
}
